import React, { forwardRef, useState, useRef, useEffect } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Skeleton,
} from '@mui/material';
import * as yup from 'yup';
import { type SubmitHandler } from 'react-hook-form';
import { compose } from '@reduxjs/toolkit';
import { isAppLoading } from '@/core/features/loader';
import Form, { type FormElement } from '@/components/Form';
import TextField from '@/components/TextField';
import SelectField from '@/components/SelectField';
import Editor from '@/components/Editor';
import {
  BookmarkIcon,
  ExtraTimeIcon,
  OfflineBoltIcon,
  UserIcon,
} from '@/static';
import { currentEvent, currentContributors } from '@/slices/events';
import { type RootState } from '@/store';
import { normalizeSimpleFormOptions, useYupValidationResolver } from '@/utils';
import { isIncidentFormValid, normalizeContributorOptions } from './utils';
import styles from './styles';

const validationSchema = yup.object({
  time: yup.string().optional(),
  action_id: yup.string().optional(),
  title: yup.string().optional(),
  content: yup.string().optional(),
  author_id: yup.string().optional(),
});

const emptyDefaultValues: FormValues = {
  time: '',
  action_id: '',
  title: '',
  content: '',
  highlighted: false,
  author_id: '',
  is_media: false,
};

export type FormValues = {
  time: string;
  action_id: string;
  title: string;
  content: string;
  highlighted: boolean;
  author_id?: string;
  is_media?: boolean;
};

type Props = ConnectedProps<typeof connector> & {
  editorId?: string;
  defaultValues?: FormValues;
  onValidationChange: (isValid: boolean) => void;
  onSubmit: SubmitHandler<FormValues>;
  resetOnSubmit?: boolean;
};

const IncidentForm = (
  {
    editorId,
    defaultValues = emptyDefaultValues,
    onValidationChange,
    onSubmit,
    resetOnSubmit,
    event,
    contributors,
    isAppLoading,
  }: Props,
  ref: React.Ref<FormElement>,
) => {
  const [formValues, setFormValues] = useState<FormValues>(defaultValues);
  const formRef = useRef<FormElement>(null);
  const resolver = useYupValidationResolver(validationSchema);
  const { highlighted } = formValues;

  useEffect(() => {
    const isValid = isAppLoading || !isIncidentFormValid(formValues);

    onValidationChange(isValid);
  }, [formValues, isAppLoading, onValidationChange]);

  const setRef = (el: FormElement) => {
    formRef.current = el;

    if (typeof ref === 'function') {
      ref(el);
    } else if (ref) {
      ref.current = el;
    }
  };

  const handleFormChange = ({ time, title, is_media }: FormValues) => {
    setFormValues((formValues) => ({
      ...formValues,
      time,
      title,
      is_media,
    }));
  };

  const handleSelectChange = (fieldName: keyof FormValues) => (event: any) => {
    const { value } = event.target;
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      [fieldName]: value,
    }));
  };

  const toggleHighlighted = () => {
    setFormValues((formValues) => ({
      ...formValues,
      highlighted: !formValues.highlighted,
    }));
  };

  const handleEditorChange = (value: string) => {
    setFormValues((formValues) => ({
      ...formValues,
      content: value,
    }));
  };

  const handleSubmit = (content: string, title?: string) => {
    const actualContent =
      typeof content === 'object' ? formValues.content : content;

    const is_media = /glomex|instagram|twitter|youtube|facebook/.test(
      actualContent,
    );

    const formValuesToSubmit: FormValues = {
      ...formValues,
      // content in editor form is object with all data,
      // in embed form it is string
      content: actualContent,
      title: title || formValues.title,
      is_media,
    };

    onSubmit(formValuesToSubmit);

    if (resetOnSubmit) {
      formRef.current?.resetForm();
      setFormValues(emptyDefaultValues);
    }
  };

  return (
    <Form
      ref={setRef}
      sx={styles.form}
      resolver={resolver}
      defaultValues={defaultValues}
      onChange={handleFormChange}
      onSubmit={handleSubmit}
    >
      {!event?.event_type?.id && (
        <>
          <Skeleton variant="rounded" height={40} />
          <Skeleton variant="rounded" height={40} />
        </>
      )}

      {event?.event_type?.id === 1 && (
        <>
          <TextField
            key="time"
            name="time"
            label=""
            autoComplete="off"
            slotProps={{
              input: {
                placeholder: 'Time',
                startAdornment: <ExtraTimeIcon />,
              },
            }}
            sx={styles.textField}
          />

          <Box sx={styles.selectWrapper}>
            <Box sx={styles.selectIcon}>
              <OfflineBoltIcon />
            </Box>

            <SelectField
              key="action_id"
              field={{
                value: formValues.action_id,
                onChange: handleSelectChange('action_id'),
                onBlur: () => {},
                name: 'action_id',
                ref: () => {},
              }}
              label="Action"
              options={normalizeSimpleFormOptions(
                event?.sport?.actions || [],
                true,
              )}
            />
          </Box>
        </>
      )}

      <Box sx={styles.titleContainer} data-field-container>
        <TextField
          name="title"
          label=""
          autoComplete="off"
          fullWidth
          slotProps={{
            input: {
              placeholder: 'Title',
            },
          }}
          sx={styles.textField}
        />
      </Box>

      <Box sx={styles.editorWrapper}>
        <FormControl>
          <Editor
            id={editorId}
            value={formValues?.content}
            onChange={handleEditorChange}
            onSubmit={handleSubmit}
            placeholder="Enter your message..."
            media
          />
        </FormControl>
      </Box>

      <Box sx={styles.selectWrapper}>
        <Box sx={styles.selectIcon}>
          <UserIcon />
        </Box>

        <SelectField
          field={{
            value: formValues.author_id,
            onChange: handleSelectChange('author_id'),
            onBlur: () => {},
            name: 'author_id',
            ref: () => {},
          }}
          label="Author"
          options={normalizeContributorOptions(contributors)}
        />
      </Box>

      <Box sx={styles.actionsWrapper}>
        <Box sx={styles.checkboxWrapper}>
          <FormControlLabel
            control={
              <Checkbox
                checked={highlighted}
                onChange={toggleHighlighted}
                sx={styles.checkbox}
              />
            }
            label="Highlight comment"
            sx={styles.checkboxLabel}
          />

          <BookmarkIcon />
        </Box>
      </Box>
    </Form>
  );
};

const connector = connect(
  (state: RootState) => ({
    event: currentEvent(state),
    contributors: currentContributors(state),
    isAppLoading: isAppLoading(state),
  }),
  null,
  null,
  { forwardRef: true },
);

export default compose(connector, forwardRef<FormElement, Props>)(IncidentForm);
